import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPredicate, IStoredPredicate } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import { Button, SegmentedControl, Skeleton, Tooltip } from '@feathr/components';

import Glue from '../../Glue';
import { createDefaultRule } from '../PredicateList.utils';
import type { IPredicateOptions } from '../usePredicateOptions';
import AddRuleButton from './AddRuleButton';
import Predicate from './Predicate';
import type { IAttrOption, IComparisonChange, TOptionsGroup } from './utils';
import {
  deriveComparisonChange,
  deriveRuleChange,
  groupToPredicateUpdate,
  isEmailTrigger,
} from './utils';

import * as styles from './Predicates.css';

interface IProps {
  disabled?: boolean;
  excludeIds?: string[];
  /** Removes this predicate, addressed by its key. */
  onClickRemove?: (key: string) => void;
  /*
   * Receives a fresh merged clone of `predicate` after every edit. The owner must swap it into a
   * NEW copy of the list, then pass that to model.set(), since set() diffs the incoming list and
   * an in-place mutation compares equal and is never marked dirty.
   */
  onEdit: (predicate: IStoredPredicate) => void;
  /** The field options to offer, shared across the list (see usePredicateOptions). */
  options: IPredicateOptions;
  optionsGroup: TOptionsGroup;
  predicate: IStoredPredicate;
}

const MAX_PREDICATE_VALUE_LENGTH = 1000;
const RULE_SKELETON_HEIGHT = 50;

function Predicates({
  disabled = false,
  excludeIds,
  onClickRemove,
  onEdit,
  options,
  optionsGroup,
  predicate,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { activities, attributes, isLoading } = options;

  /*
   * Email triggers only include (no Includes/Excludes toggle) and don't grow
   * into rule groups; every other options group offers both affordances.
   */
  const isTrigger = isEmailTrigger(optionsGroup);
  const isExcludes = predicate.excludes || false;
  // A single predicate renders as a one-rule group; rules carry their own keys.
  const localGroup = Number(predicate.group?.length) > 0 ? predicate.group! : [predicate];

  // Every edit emits a merged clone through onEdit; this component never writes the predicate.
  const updatePredicate = (updateData: Partial<IStoredPredicate>): void => {
    onEdit({ ...predicate, ...updateData });
  };

  const handleMatchModeChange = (value: string): void => {
    updatePredicate({ excludes: value === 'excludes' });
  };

  const handleRemoveFilterSet = (): void => {
    onClickRemove?.(predicate.key);
  };

  const handleAddFilter = (): void => {
    const newRule = createDefaultRule(predicate.kind);

    if (Number(predicate.group?.length) > 0) {
      updatePredicate(groupToPredicateUpdate([...localGroup, newRule]));
      return;
    }
    /*
     * Convert the single predicate to a group: the predicate's fields move into
     * the group as its first rule — a new node, so it mints its own key — while
     * the container keeps the row's key (edits resolve the row by it) plus its
     * container-level fields.
     */
    const { excludes, group, group_mode, key, ...rule } = predicate;
    updatePredicate(groupToPredicateUpdate([ensurePredicateKeys(rule), newRule]));
  };

  const handleRemoveFilter = (key: string): void => {
    updatePredicate(groupToPredicateUpdate(localGroup.filter((rule) => rule.key !== key)));
  };

  /*
   * Apply a change to the rule addressed by key — not by render index: the
   * render map filters out hidden rules (form_type), so its index does not line
   * up with positions in the unfiltered group. A deferred edit (e.g. a debounced
   * value flush) for a rule that has since been removed finds no match and is
   * dropped rather than resurrecting the rule.
   */
  const updateRuleAt = (key: string, changes: Partial<IStoredPredicate>): void => {
    const groupIndex = localGroup.findIndex((rule) => rule.key === key);
    if (groupIndex === -1) {
      return;
    }
    const updatedGroup = localGroup.map((rule, i) =>
      i === groupIndex ? { ...rule, ...changes } : rule,
    );
    updatePredicate(groupToPredicateUpdate(updatedGroup));
  };

  const filters = (
    <div className={styles.predicateGroup}>
      {!isTrigger && (
        <div className={styles.matchModeControl}>
          <SegmentedControl
            data={[
              {
                label: t('Includes'),
                value: 'includes',
              },
              {
                label: t('Excludes'),
                value: 'excludes',
              },
            ]}
            name={`predicate_match_mode_${predicate.key}`}
            onChange={handleMatchModeChange}
            value={isExcludes ? 'excludes' : 'includes'}
          />
        </div>
      )}
      {localGroup
        .filter((item) => item.attr_against !== 'form_type')
        .map((item, index, visibleGroup) => {
          const lastItemIndex = visibleGroup.length - 1;

          function handleChangeRule(rule: IAttrOption): void {
            updateRuleAt(item.key, deriveRuleChange(rule, { optionsGroup }, t));
          }

          function handleChangeComparison(change: IComparisonChange): void {
            updateRuleAt(item.key, deriveComparisonChange(item, change));
          }

          function handleChangeValue(value: IPredicate['value']): void {
            const valueLength = typeof value === 'string' ? value.length : 0;

            if (valueLength <= MAX_PREDICATE_VALUE_LENGTH) {
              updateRuleAt(item.key, { value });
            }
          }

          function handleRemove(): void {
            handleRemoveFilter(item.key);
          }

          const props = {
            activities,
            attributes,
            disabled,
            excludeIds,
            optionsGroup,
            handleChangeRule,
            handleChangeComparison,
            handleChangeValue,
            groupKind: localGroup.length > 1 ? localGroup[0].kind : undefined,
          };

          return (
            <Fragment key={item.key}>
              <div className={localGroup.length === 1 ? undefined : styles.predicateWrapper}>
                {isLoading ? (
                  <Skeleton height={RULE_SKELETON_HEIGHT} />
                ) : (
                  <Predicate predicate={item} {...props} />
                )}
                {!disabled && !isLoading && localGroup.length !== 1 && (
                  <Tooltip title={t('Remove filter rule')}>
                    <Button
                      className={styles.singleClose}
                      name={'remove_filter_rule'}
                      onClick={handleRemove}
                      type={'icon'}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </Tooltip>
                )}
              </div>
              <Glue isLastItem={index === lastItemIndex} mode={predicate?.group_mode} t={t} />
            </Fragment>
          );
        })}
    </div>
  );

  const actions = (
    <span className={styles.actions}>
      {!isTrigger && <AddRuleButton onClick={handleAddFilter} />}
      {onClickRemove && (
        <Tooltip title={t('Remove filter')}>
          <Button name={'remove_filter'} onClick={handleRemoveFilterSet} type={'icon'}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Tooltip>
      )}
    </span>
  );

  return (
    <div className={classNames(styles.root, { [styles.disabled]: disabled })}>
      {filters}
      {!disabled && actions}
    </div>
  );
}

export default observer(Predicates);
